import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

interface LegoCardProps {
  children: React.ReactNode;
  className?: string;
  intensity?: 'light' | 'medium' | 'strong';
  color?: 'primary' | 'secondary' | 'accent' | 'neutral';
  onClick?: () => void;
  style?: React.CSSProperties;
}

const LegoCardContainer = styled(motion.div)<{
  $intensity: 'light' | 'medium' | 'strong';
  $color: 'primary' | 'secondary' | 'accent' | 'neutral';
  $clickable: boolean;
}>`
  position: relative;
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing[6]};
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};
  
  /* Couleurs selon la variante */
  background: ${props => {
    switch (props.$color) {
      case 'primary': return props.theme.gradients.primary;
      case 'secondary': return props.theme.gradients.sunset;
      case 'accent': return props.theme.gradients.ocean;
      default: return props.theme.colors.neutral[50];
    }
  }};
  
  color: ${props => props.$color === 'neutral' ? props.theme.colors.text : 'white'};
  
  /* Effet LEGO selon l'intensité */
  ${props => {
    const intensity = props.$intensity;
    
    if (intensity === 'light') {
      return `
        /* Base plate légère */
        box-shadow: 
          0 6px 12px rgba(0, 0, 0, 0.12),
          0 3px 6px rgba(0, 0, 0, 0.08);
        
        /* Relief subtil */
        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: ${props.theme.borderRadius.lg};
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.08) 0%,
            rgba(255, 255, 255, 0.03) 50%,
            rgba(0, 0, 0, 0.03) 100%
          );
          pointer-events: none;
          z-index: 1;
        }
        
        &:hover {
          transform: translateY(-1px) translateZ(2px);
          box-shadow: 
            0 8px 16px rgba(0, 0, 0, 0.15),
            0 4px 8px rgba(0, 0, 0, 0.1);
        }
      `;
    }
    
    if (intensity === 'medium') {
      return `
        /* Base plate moyenne */
        box-shadow: 
          0 8px 16px rgba(0, 0, 0, 0.15),
          0 4px 8px rgba(0, 0, 0, 0.1),
          0 2px 4px rgba(0, 0, 0, 0.05);
        
        /* Relief visible */
        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: ${props.theme.borderRadius.lg};
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.12) 0%,
            rgba(255, 255, 255, 0.06) 50%,
            rgba(0, 0, 0, 0.06) 100%
          );
          pointer-events: none;
          z-index: 1;
        }
        
        /* Bordure de profondeur */
        &::after {
          content: '';
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          border-radius: ${props.theme.borderRadius.lg};
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.25) 0%,
            rgba(255, 255, 255, 0.1) 25%,
            rgba(0, 0, 0, 0.1) 75%,
            rgba(0, 0, 0, 0.15) 100%
          );
          z-index: -1;
          opacity: 0.7;
        }
        
        &:hover {
          transform: translateY(-2px) translateZ(4px);
          box-shadow: 
            0 12px 24px rgba(0, 0, 0, 0.2),
            0 6px 12px rgba(0, 0, 0, 0.15),
            0 3px 6px rgba(0, 0, 0, 0.1);
          
          &::after {
            opacity: 0.9;
            transform: translateZ(3px);
          }
        }
      `;
    }
    
    if (intensity === 'strong') {
      return `
        /* Base plate prononcée */
        box-shadow: 
          0 12px 24px rgba(0, 0, 0, 0.18),
          0 6px 12px rgba(0, 0, 0, 0.12),
          0 3px 6px rgba(0, 0, 0, 0.08),
          0 1px 3px rgba(0, 0, 0, 0.04);
        
        /* Relief très visible */
        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: ${props.theme.borderRadius.lg};
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.15) 0%,
            rgba(255, 255, 255, 0.08) 50%,
            rgba(0, 0, 0, 0.08) 100%
          );
          pointer-events: none;
          z-index: 1;
        }
        
        /* Bordure de profondeur forte */
        &::after {
          content: '';
          position: absolute;
          top: -3px;
          left: -3px;
          right: -3px;
          bottom: -3px;
          border-radius: ${props.theme.borderRadius.lg};
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.3) 0%,
            rgba(255, 255, 255, 0.15) 25%,
            rgba(0, 0, 0, 0.15) 75%,
            rgba(0, 0, 0, 0.25) 100%
          );
          z-index: -1;
          opacity: 0.8;
        }
        
        &:hover {
          transform: translateY(-3px) translateZ(6px);
          box-shadow: 
            0 16px 32px rgba(0, 0, 0, 0.25),
            0 8px 16px rgba(0, 0, 0, 0.18),
            0 4px 8px rgba(0, 0, 0, 0.12),
            0 2px 4px rgba(0, 0, 0, 0.06);
          
          &::after {
            opacity: 1;
            transform: translateZ(4px);
          }
        }
      `;
    }
  }}
`;

const LegoCardContent = styled.div`
  position: relative;
  z-index: 2;
`;

const LegoCard: React.FC<LegoCardProps> = ({
  children,
  className,
  intensity = 'medium',
  color = 'neutral',
  onClick,
  style
}) => {
  return (
    <LegoCardContainer
      className={className}
      style={style}
      $intensity={intensity}
      $color={color}
      $clickable={!!onClick}
      onClick={onClick}
      whileHover={onClick ? { scale: 1.02 } : {}}
      whileTap={onClick ? { scale: 0.98 } : {}}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <LegoCardContent>
        {children}
      </LegoCardContent>
    </LegoCardContainer>
  );
};

export default LegoCard;
